import * as React from 'react';

import { Button } from '@rematter/ui-web';
import { useAtom, useSetAtom } from 'jotai';

import { useWeightUnitManager } from '../../hooks/useWeightUnitManager';
import { SalesOrderLineItemRecord } from '../../types/salesOrder';
import { SalesOrderMaterialsTable } from '../_shared/LineItems';
import { WeightUnitSwitch } from '../_shared/WeightUnitSwitch';

import { convertToLoadFormLineItem } from './helpers/convertSalesOrderToLoadLineItem';
import { useSalesOrderMaterialsErrors } from './hooks/useSalesOrderMaterialsErrors';

import {
  $addLineItem,
  $removeLineItem,
  $salesOrderForm,
  $updateLineItem,
  $updateSalesOrderForm,
} from '~/modules/trade/_state/salesOrderForm.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import {
  enumLoadWeightType,
  LoadFormLineItem,
} from '~/modules/trade/types/load';

export const SalesOrderWizardStepMaterials = () => {
  const t = useTranslations();
  const [formState] = useAtom($salesOrderForm);
  const updateForm = useSetAtom($updateSalesOrderForm);
  const [, addLineItem] = useAtom($addLineItem);
  const [, updateLineItem] = useAtom($updateLineItem);
  const [, removeLineItem] = useAtom($removeLineItem);

  // Use ref to store latest form state for stable callbacks
  const formRef = React.useRef(formState);
  React.useEffect(() => {
    formRef.current = formState;
  }, [formState]);

  const { currentWeightUnit, handleWeightUnitChange } = useWeightUnitManager({
    weightDisplayUnitPreference: formState.weightDisplayUnit ?? null,
    onWeightDisplayUnitChange: (unit) =>
      updateForm({ weightDisplayUnit: unit }),
    defaultUnit: 'PriceUnit',
  });

  const handleAddMaterial = React.useCallback(() => {
    addLineItem();
  }, [addLineItem]);

  const handleUpdateLineItem = React.useCallback(
    (lineItemId: string, updates: Partial<LoadFormLineItem>) => {
      // Use ref to access current form state without creating dependency
      const index = formRef.current.lineItems.findIndex(
        (item) => item.id === lineItemId,
      );
      if (index === -1) return;

      const updatedItem: Partial<SalesOrderLineItemRecord> = {};

      if (updates.itemId !== undefined) {
        updatedItem.itemId = updates.itemId;
      }

      if (updates.weights?.[enumLoadWeightType.Net] != null) {
        updatedItem.quantity =
          updates.weights?.[enumLoadWeightType.Net]?.value ?? 0;
      }

      if (updates.unitPrice !== undefined && updates.unitPrice !== null) {
        updatedItem.unitPrice = updates.unitPrice;
      }

      if (updates.pricingUnit !== undefined && updates.pricingUnit !== null) {
        updatedItem.unit = updates.pricingUnit;
      }

      updateLineItem({ index, updates: updatedItem });
    },
    [updateLineItem],
  );

  const handleDeleteLineItem = React.useCallback(
    (lineItemId: string) => {
      // Use ref to access current form state without creating dependency
      const index = formRef.current.lineItems.findIndex(
        (item) => item.id === lineItemId,
      );
      if (index !== -1) {
        removeLineItem(index);
      }
    },
    [removeLineItem],
  );

  // Convert line items to the format expected by the table
  // Use content-based memoization instead of reference-based to break the loop
  const loadFormLineItems = React.useMemo(
    () => formState.lineItems.map(convertToLoadFormLineItem),
    [formState.lineItems],
  );

  const materialsErrors = useSalesOrderMaterialsErrors(formState.errors);

  return (
    <div className="@SalesOrderWizardStepMaterials flex flex-col gap-6">
      {loadFormLineItems.length === 0 ? (
        <div className="py-12 text-center">
          <div className="mb-6 text-neutral/50">
            <p className="mb-2 text-lg">
              {t.salesOrdersView.wizard.materials.emptyState.title}
            </p>
            <p className="text-sm">
              {t.salesOrdersView.wizard.materials.emptyState.description}
            </p>
          </div>
          <Button
            variant="outline"
            color="primary"
            onPress={handleAddMaterial}
            size="large"
          >
            {t.salesOrdersView.wizard.materials.buttons.addMaterial}
          </Button>
        </div>
      ) : (
        <div>
          <div className="mb-4 flex justify-between">
            <h3 className="text-lg font-semibold">
              {t.itemTable.salesOrderTitle}
            </h3>

            <WeightUnitSwitch
              value={currentWeightUnit}
              onChange={handleWeightUnitChange}
            />
          </div>
          <SalesOrderMaterialsTable
            lineItems={loadFormLineItems}
            onUpdateLineItem={handleUpdateLineItem}
            onDeleteLineItem={handleDeleteLineItem}
            onAddLineItem={handleAddMaterial}
            disabled={formState.isSubmitting}
            weightUnit={currentWeightUnit}
            errors={materialsErrors}
          />
        </div>
      )}
    </div>
  );
};
